import React, { useEffect, useState, useRef } from "react";
import { FaRegEdit, FaUserGraduate, FaDownload } from "react-icons/fa";
import { RiDeleteBin6Line, RiExchangeFundsLine } from "react-icons/ri";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import * as XLSX from "xlsx";

import ClientActiveStatusModal from "../../Modals/ClientActiveStatusModal";
import DeleteClientModal from "../../Modals/DeleteClientModal";
import EditClientModal from "../../Modals/EditClientModal";
import ClientDetailsModal from "../../Modals/ClientDetailsModal";

const CertificateExpiring = ({
  query,
  page,
  itemsPerPage,
  onDataChange,
  onCertificateDataChange,
  dateFilter,
  certificateFilter,
}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const previousDateFilter = useRef(null);
  const previousCertificateFilter = useRef(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setClients([]);

      let url = `${process.env.REACT_APP_API_URL}/our-client/get-clients`;

      if (certificateFilter) {
        // Fetch ALL clients first, then filter by certificate name on frontend
        const res = await axios.get(url);

        // Filter clients that have ISO 9001:2015 certificate with isExpiring: true
        const certificateFilteredClients = res.data.filter((client) => {
          if (!client.servicesTaken || client.servicesTaken.length === 0) {
            return false;
          }

          return client.servicesTaken.some(
            (service) =>
              (service.certificateId?.certificateName === "ISO 9001:2015" ||
                service.certificateId?.name === "ISO 9001:2015") &&
              service.isExpiring === true
          );
        });

        setClients(certificateFilteredClients);
      } else if (dateFilter) {
        // Fetch clients by date filter
        url = `${process.env.REACT_APP_API_URL}/our-client/filter/expiry?`;

        if (dateFilter.type === "single") {
          const fromDate = `${dateFilter.year}-${dateFilter.month}-01`;
          const toDate = new Date(
            dateFilter.year,
            parseInt(dateFilter.month),
            0
          )
            .toISOString()
            .split("T")[0];
          url += `from=${fromDate}&to=${toDate}`;
        } else if (dateFilter.type === "range") {
          const fromDate = `${dateFilter.startYear}-${dateFilter.startMonth}-01`;
          const toDate = new Date(
            dateFilter.endYear,
            parseInt(dateFilter.endMonth),
            0
          )
            .toISOString()
            .split("T")[0];
          url += `from=${fromDate}&to=${toDate}`;
        }

        const res = await axios.get(url);
        // Filter for clients with expiring certificates
        const expiringClients = res.data.filter((client) =>
          client.servicesTaken?.some((service) => service.isExpiring === true)
        );
        setClients(expiringClients);
      } else {
        // Fetch all clients with expiring certificates
        const res = await axios.get(url);
        const expiringClients = res.data.filter((client) =>
          client.servicesTaken?.some((service) => service.isExpiring === true)
        );
        setClients(expiringClients);
      }
    } catch (err) {
      console.error("Error fetching certificate expiring clients:", err);
    } finally {
      setLoading(false);
    }
  };

  // Excel Export Function
  const handleExportToExcel = () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const exportData = [];

    filteredData.forEach((client) => {
      if (client.servicesTaken && client.servicesTaken.length > 0) {
        // Only export expiring services
        const expiringServices = client.servicesTaken.filter(
          (service) => service.isExpiring === true
        );

        expiringServices.forEach((service) => {
          const daysUntilExpiry = service.expiryDate
            ? Math.ceil(
                (new Date(service.expiryDate) - new Date()) /
                  (1000 * 60 * 60 * 24)
              )
            : "N/A";

          exportData.push({
            "Client Name": client.clientName || client.name || "",
            Phone: client.phone || client.phoneNumber || client.mobile || "",
            Email: client.email || "",
            Certificate:
              service.certificateId?.certificateName ||
              service.certificateId?.name ||
              "",
            Status: client.status || "",
            "Expiry Date": service.expiryDate
              ? new Date(service.expiryDate).toLocaleDateString()
              : "",
            "Days Until Expiry": daysUntilExpiry,
            "Issue Date":
              service.issuedDate || service.issueDate
                ? new Date(
                    service.issuedDate || service.issueDate
                  ).toLocaleDateString()
                : "",
          });
        });
      }
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Certificate Expiring Clients");

    // Auto-size columns
    const colWidths = [
      { wch: 25 }, // Client Name
      { wch: 15 }, // Phone
      { wch: 30 }, // Email
      { wch: 25 }, // Certificate
      { wch: 12 }, // Status
      { wch: 15 }, // Expiry Date
      { wch: 18 }, // Days Until Expiry
      { wch: 15 }, // Issue Date
    ];
    ws["!cols"] = colWidths;

    // Generate filename with current date
    const today = new Date().toISOString().split("T")[0];
    const filename = `Certificate_Expiring_Clients_${today}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  };

  // Only fetch data when filters actually change
  useEffect(() => {
    const dateFilterChanged =
      JSON.stringify(dateFilter) !== JSON.stringify(previousDateFilter.current);
    const certificateFilterChanged =
      JSON.stringify(certificateFilter) !==
      JSON.stringify(previousCertificateFilter.current);

    if (dateFilterChanged || certificateFilterChanged) {
      previousDateFilter.current = dateFilter;
      previousCertificateFilter.current = certificateFilter;
      fetchData();
    }
  }, [dateFilter, certificateFilter]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Calculate filtered data without causing re-renders
  const filteredData = React.useMemo(() => {
    return clients.filter((item) =>
      (item?.clientName || item?.name || "")
        .toLowerCase()
        .includes((query || "").toLowerCase())
    );
  }, [clients, query]);

  // Calculate pagination data
  const { paginatedClients, totalPages } = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filteredData.slice(startIndex, endIndex);
    const total = Math.ceil(filteredData.length / itemsPerPage);

    return {
      paginatedClients: paginated,
      totalPages: total,
    };
  }, [filteredData, page, itemsPerPage]);

  // Only notify parent when pagination data actually changes
  useEffect(() => {
    if (!loading && clients.length >= 0) {
      onDataChange(totalPages, filteredData.length, filteredData);
      // Pass certificate filtered data to parent
      if (certificateFilter && onCertificateDataChange) {
        onCertificateDataChange(filteredData);
      }
    }
  }, [totalPages, filteredData.length, loading, certificateFilter]);

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setDeleteModalOpen(true);
  };

  const handleStatusClick = (client) => {
    setSelectedClient(client);
    setStatusModalOpen(true);
  };

  const handleDetailsClick = (client) => {
    setSelectedClient(client);
    setDetailsModalOpen(true);
  };

  const closeModals = () => {
    setStatusModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setDetailsModalOpen(false);
    setSelectedClient(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedClient || !selectedClient._id) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/our-client/delete-client/${selectedClient._id}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          data: {
            email: selectedClient.email || "",
          },
        }
      );
      closeModals();
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete client.");
    }
  };

  // Helper function to get days until expiry for a client
  const getDaysUntilExpiry = (client) => {
    if (!client.servicesTaken) return null;

    const expiringService = client.servicesTaken.find(
      (service) => service.isExpiring === true
    );

    if (!expiringService || !expiringService.expiryDate) return null;

    const today = new Date();
    const expiryDate = new Date(expiringService.expiryDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  // Helper function to get expiry status color
  const getExpiryStatusColor = (days) => {
    if (days <= 7) return "bg-red-100 text-red-800 border-red-200";
    if (days <= 30) return "bg-orange-100 text-orange-800 border-orange-200";
    return "bg-yellow-100 text-yellow-800 border-yellow-200";
  };

  return (
    <section className="px-2 mb-4">
      {/* Export Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportToExcel}
          disabled={loading || filteredData.length === 0}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 transition-colors duration-200"
        >
          <FaDownload className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-2">
          {Array.from({ length: itemsPerPage }).map((_, idx) => (
            <div
              key={idx}
              className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center"
            >
              <Skeleton circle width={80} height={80} className="mb-4" />
              <Skeleton width={120} height={20} />
              <Skeleton width={180} height={15} className="mt-2" />
              <Skeleton width="100%" height={35} className="mt-6" />
            </div>
          ))}
        </div>
      ) : paginatedClients.length === 0 ? (
        <div className="flex justify-center">
          <p className="p-4 text-gray-500">
            No clients with expiring certificates found.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto sm:overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-w-[300px] sm:min-w-0 py-2">
            {paginatedClients.map((client, index) => {
              const daysUntilExpiry = getDaysUntilExpiry(client);
              const expiryStatusColor = daysUntilExpiry
                ? getExpiryStatusColor(daysUntilExpiry)
                : "bg-gray-100 text-gray-800 border-gray-200";

              return (
                <div
                  key={client._id || index}
                  className="relative bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center border-2"
                >
                  <div className="absolute top-2 right-2 flex gap-2">
                    {/* <button
                      className="text-blue-600"
                      onClick={() => handleEditClick(client)}
                      aria-label="Edit client"
                    >
                      <FaRegEdit className="h-5 w-5" />
                    </button> */}
                    <button
                      className="text-red-600"
                      onClick={() => handleDeleteClick(client)}
                      aria-label="Delete client"
                    >
                      <RiDeleteBin6Line className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Expiry Warning Badge */}
                  <div className="absolute top-2 left-2 right-2 flex justify-between">
                    {/* Expiry Status - Left */}
                    <p
                      className={`rounded-2xl text-xs font-normal tracking-wide py-1 px-2 border ${expiryStatusColor}`}
                    >
                      {daysUntilExpiry !== null
                        ? daysUntilExpiry > 0
                          ? `${daysUntilExpiry} days left`
                          : "Expired"
                        : "Expiring Soon"}
                    </p>

                    {/* Client Status - Right */}
                    {/* <p
                      className={`${
                        client.status === "active"
                          ? "bg-green-600"
                          : "bg-gray-600"
                      } rounded-2xl text-white text-xs font-normal tracking-wide py-1 px-2`}
                    >
                      {client.status || "Unknown"}
                    </p> */}
                  </div>

                  <FaUserGraduate className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-white shadow-md text-[#1B81B2] mt-8" />

                  <h3 className="font-semibold text-gray-900">
                    {client.clientName || client.name}
                  </h3>
                  <p className="text-sm text-blue-600 mt-1 bg-blue-50 px-3 py-1 rounded-full">
                    {client.email}
                  </p>

                  {/* Show expiring certificate info */}
                  {/* {client.servicesTaken && (
                    <div className="mt-2 text-xs text-gray-600">
                      {client.servicesTaken
                        .filter((service) => service.isExpiring === true)
                        .map((service, idx) => (
                          <div
                            key={idx}
                            className="bg-orange-50 px-2 py-1 rounded mb-1"
                          >
                            {service.certificateId?.certificateName ||
                              service.certificateId?.name ||
                              "Certificate"}{" "}
                            expiring
                          </div>
                        ))}
                    </div>
                  )} */}

                  <div className="w-full flex justify-between gap-4 px-4">
                    <div className="w-full">
                      <button
                        type="button"
                        onClick={() => handleDetailsClick(client)}
                        className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium py-1.5 rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-300"
                      >
                        Details
                      </button>
                    </div>
                    {/* <div>
                      <button
                        type="button"
                        onClick={() => handleStatusClick(client)}
                        className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium px-1.5 py-1.5 rounded-lg text-sm md:text-base shadow-md hover:shadow-lg transition-all duration-300"
                        aria-label="Change client status"
                      >
                        <RiExchangeFundsLine size={20} />
                      </button>
                    </div> */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {statusModalOpen && selectedClient && (
        <ClientActiveStatusModal
          client={selectedClient}
          onClose={closeModals}
        />
      )}

      {deleteModalOpen && selectedClient && (
        <DeleteClientModal
          itemName={selectedClient.clientName || selectedClient.name}
          onClose={closeModals}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {editModalOpen && selectedClient && (
        <EditClientModal
          client={selectedClient}
          onClose={closeModals}
          onSubmit={() => {
            closeModals();
          }}
        />
      )}

      {detailsModalOpen && selectedClient && (
        <div>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
            <div className="max-w-full w-full sm:max-w-lg">
              <ClientDetailsModal
                client={selectedClient}
                onClose={closeModals}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CertificateExpiring;
